import React, { useMemo } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import config from '@/config'
import ThemeInterface from '@/theme-interface'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import CheckIcon from '@assets/images/icons/check_medium.svg'
import { SignatureItem } from '@/modules/signature/types'
import SignatureSigningMoreButton from './singature-signing-more-button'
import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import { getContrastText } from '@/modules/base/helpers/getContrastText'
import { getFieldTypeInfo } from '@/modules/signature/_helpers/getFieldTypeInfo'
import TextEllipsis from '@/modules/base/components/texts/text-ellipsis'

const { dateFormat: { client: clientDateFormat } } = config

type SignatureSigningItemStyleProps = {
  signatureColor: string,
  contrastColor: string,
  active: boolean,
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    fontSize: theme.fontSizes.small,
    marginBottom: 10,
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  content: ({ signatureColor, active, contrastColor }: SignatureSigningItemStyleProps) => ({
    display: 'flex',
    flex: 1,
    minWidth: 0,
    padding: '10px 20px',
    backgroundColor: signatureColor ?? theme.white,
    border: `1px solid ${active ? theme.black : signatureColor}`,
    borderRadius: 4,
    color: contrastColor,
    cursor: 'pointer',
  }),
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 20,
  },
  details: {
    display: 'flex',
    gap: 5,
    fontSize: theme.fontSizes.extrasmall,
  },
  signingInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  label: {
    flex: 1,
    minWidth: 0,
  },
  signerName: {
    width: 230,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: theme.fontSizes.extrasmall,
  },
  pageNumber: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  action: {
    marginLeft: 15,
  },
  signed: {
    borderColor: theme.borderColors.default,
  },
}), { name: 'SignatureSigningItem' })

type SignatureSigningItemProps = {
  index: number,
  isUserCreator: boolean,
  signatureItem: SignatureItem,
  active: boolean,
  readOnly: boolean,
}

const SignatureSigningItem = (props: SignatureSigningItemProps) => {
  const { isUserCreator, index, signatureItem, readOnly, active } = props
  const {
    name,
    type,
    position,
    signature: signing,
    signed,
    date,
    text,
    signedAt,
    color: signatureColor,
  } = signatureItem
  const contrastColor = getContrastText(signatureColor)
  const classes = useStyles({ signatureColor, active, contrastColor })
  const isSigned = signing || signed || date || text
  const {
    setCurrentItemIndex,
    pager: { setCurrentPage },
  } = useSignatureContext()

  const icon = useMemo(() => {
    if (isSigned) { return CheckIcon }
    return getFieldTypeInfo(signatureItem).icon
  }, [isSigned])
  const itemLabel = useMemo(() => {
    return getFieldTypeInfo(signatureItem).label
  }, [])
  const formattedSignedAt = signedAt && moment(signedAt).format(clientDateFormat)
  const signerName = !name?.trim() ? lang.signature.signing.defaultSignerName : name

  const onShow = (): void => {
    setCurrentPage(position.page)
    setCurrentItemIndex(index)
  }

  const moreButton = !readOnly && (
    <div className={classes.action}>
      <SignatureSigningMoreButton
        type={type}
        index={index}
        disabled={!isSigned}
        signatureItem={signatureItem}
      />
    </div>
  )

  const rootClass = clsx(classes.root)
  const contentClass = clsx(classes.content, signing && classes.signed)
  return (
    <div className={rootClass}>
      <div className={contentClass} onClick={onShow}>
        <div className={classes.icon}>
          <SVGIcon
            svg={icon}
            height="13px"
            width="13px"
            fill={contrastColor} />
        </div>
        <div className={classes.signingInfo}>
          <TextEllipsis className={classes.label}>{itemLabel}</TextEllipsis>
          <div className={classes.details}>
            <div className={classes.signerName}>{signerName} ({index + 1})</div>
            {(formattedSignedAt && isUserCreator) && (
              <>
                |
                <div dangerouslySetInnerHTML={{ __html: lang.signature.signing.state.done(formattedSignedAt, type) }}></div>
              </>
            )}
          </div>
        </div>
        <div className={classes.pageNumber}>{lang.signature.page} {position.page}</div>
      </div>
      {moreButton}
    </div>
  )
}

export default SignatureSigningItem
